/// <reference types="../../vite-env" />

/**
 * Axios interceptors configuration
 * Handles request/response transformations and elegant error handling
 */
import { AxiosError, InternalAxiosRequestConfig, AxiosResponse } from 'axios';
import { axiosInstance } from '@core/config/axios.config';
import { useAuthStore } from '@store/auth.store';
import { startTokenRefreshScheduler, stopTokenRefreshScheduler } from '@services/api/tokenRefreshScheduler';
import { enqueueSnackbar } from 'notistack';

/**
 * Get error message from API response (FastAPI uses `detail`)
 */
const getErrorMessage = (error: AxiosError): string => {
    if (error.response?.data) {
        const data = error.response.data as any;
        if (data.detail != null) {
            if (typeof data.detail === 'string') {
                return data.detail;
            }
            if (Array.isArray(data.detail) && data.detail[0]?.msg) {
                return data.detail.map((d: { msg?: string }) => d.msg).filter(Boolean).join(', ');
            }
        }
        return data.message || data.error || 'An error occurred';
    }
    return error.message || 'Network error occurred';
};

/**
 * Request interceptor to add authentication token
 */
export const requestInterceptor = (
    config: InternalAxiosRequestConfig
): InternalAxiosRequestConfig => {
    // Add authentication token
    // Try store first, then localStorage as fallback (in case store hasn't synced yet)
    const { accessToken } = useAuthStore.getState();
    const token = accessToken || localStorage.getItem('accessToken');
    if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
    }

    // Add custom headers
    if (config.headers) {
        config.headers['X-Request-Time'] = new Date().toISOString();
        config.headers['X-Client-Version'] = window._env_.FTDQADC_APP_VERSION || '1.0.0';
    }

    // Log request in development
    if (import.meta.env.DEV) {
        console.log(`📤 ${config.method?.toUpperCase()} ${config.url}`, {
            headers: config.headers,
            data: config.data,
        });
    }

    return config;
};

/**
 * Request error interceptor
 */
export const requestErrorInterceptor = (error: AxiosError): Promise<AxiosError> => {
    console.error('❌ Request Error:', error);
    enqueueSnackbar('Failed to send request', { variant: 'error' });
    return Promise.reject(error);
};

/**
 * Response interceptor to transform responses
 */
export const responseInterceptor = (response: AxiosResponse): AxiosResponse => {
    // Log response in development
    if (import.meta.env.DEV) {
        console.log(`📥 ${response.config.method?.toUpperCase()} ${response.config.url}`, {
            status: response.status,
            data: response.data,
        });
    }

    return response;
};

/**
 * Error interceptor to handle errors elegantly
 */
export const errorInterceptor = async (error: AxiosError): Promise<never> => {
    const { response, request, config } = error;

    // Network error (no response from server)
    if (!response) {
        if (!request) {
            // Request setup failed
            console.error('❌ Request Setup Error:', error.message);
            enqueueSnackbar('Failed to setup request', { variant: 'error' });
        } else {
            // Network error - no response received
            console.error('❌ Network Error:', error.message);
            enqueueSnackbar('Network error. Please check your connection.', {
                variant: 'error',
                autoHideDuration: 5000,
            });
        }
        return Promise.reject(error);
    }

    // Response received with error status
    const status = response.status;
    const errorMessage = getErrorMessage(error);
    const url = config?.url || 'unknown';

    // Log error in development
    if (import.meta.env.DEV) {
        const errCode =
            response.data &&
            typeof response.data === 'object' &&
            'error_code' in response.data &&
            typeof (response.data as { error_code?: unknown }).error_code === 'string'
                ? (response.data as { error_code: string }).error_code
                : undefined;
        console.error(`❌ ${status} Error on ${url}:`, {
            status,
            message: errorMessage,
            error_code: errCode,
            data: response.data,
        });
    }

    // Handle specific status codes
    switch (status) {
        case 400:
            // Bad Request
            enqueueSnackbar(errorMessage || 'Invalid request', {
                variant: 'error',
            });
            break;

        case 401:
            // Unauthorized - Try to refresh token first
            const originalRequest = config as InternalAxiosRequestConfig & { _retry?: boolean };
            
            // Skip refresh if this is already a retry or if it's the refresh endpoint itself
            if (originalRequest._retry || originalRequest.url?.includes('/auth/refresh')) {
                // Refresh failed or this is the refresh endpoint - clear auth and redirect
                console.warn('🔒 Token refresh failed or refresh endpoint error - clearing auth');
                stopTokenRefreshScheduler();
                useAuthStore.getState().clearAuth();

                if (!window.location.pathname.includes('/login')) {
                    window.location.href = '/login';
                }
                break;
            }

            // Try to refresh the token
            const refreshToken = localStorage.getItem('refreshToken');
            if (refreshToken && originalRequest) {
                const baseURL = axiosInstance.defaults.baseURL || 'http://localhost:8000/api/v1';
                const axios = (await import('axios')).default;

                const attemptRefresh = async (): Promise<string | null> => {
                    const refreshResponse = await axios.post(
                        `${baseURL}/auth/refresh`,
                        { refresh_token: refreshToken },
                        { headers: { 'Content-Type': 'application/json' } }
                    );
                    const newAccessToken = refreshResponse.data?.access_token || refreshResponse.data?.accessToken;
                    return newAccessToken || null;
                };

                const applyTokenAndRetry = (newAccessToken: string): Promise<unknown> => {
                    localStorage.setItem('accessToken', newAccessToken);
                    const authStore = useAuthStore.getState();
                    const currentUser = authStore.user;
                    if (currentUser) {
                        authStore.setAuth(currentUser, newAccessToken, refreshToken);
                    }
                    if (originalRequest.headers) {
                        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
                    }
                    startTokenRefreshScheduler();
                    return axiosInstance(originalRequest);
                };

                try {
                    originalRequest._retry = true;
                    const newAccessToken = await attemptRefresh();
                    if (newAccessToken) {
                        return applyTokenAndRetry(newAccessToken) as Promise<never>;
                    }
                    throw new Error('No access token in refresh response');
                } catch (refreshError) {
                    const status = axios.isAxiosError(refreshError) ? refreshError.response?.status : undefined;
                    const isTemporary = status === 502 || status === 503 || status === 504;

                    if (isTemporary) {
                        // DB/server temporarily unavailable - retry once without showing message
                        try {
                            await new Promise((r) => setTimeout(r, 2000));
                            const retryToken = await attemptRefresh();
                            if (retryToken) {
                                return applyTokenAndRetry(retryToken) as Promise<never>;
                            }
                        } catch {
                            // Retry also failed - show message and redirect to login
                        }
                        enqueueSnackbar('Service temporarily unavailable. Please try again later.', {
                            variant: 'warning',
                            autoHideDuration: 6000,
                        });
                        stopTokenRefreshScheduler();
                        localStorage.removeItem('accessToken');
                        localStorage.removeItem('refreshToken');
                        useAuthStore.getState().clearAuth();
                        if (!window.location.pathname.includes('/login')) {
                            window.location.href = '/login';
                        }
                        return Promise.reject(refreshError);
                    }

                    // Real auth failure (401, 403, invalid token, etc.) - clear auth and redirect
                    console.warn('Token refresh failed (auth error):', refreshError);
                    stopTokenRefreshScheduler();
                    localStorage.removeItem('accessToken');
                    localStorage.removeItem('refreshToken');
                    useAuthStore.getState().clearAuth();

                    if (!window.location.pathname.includes('/login')) {
                        window.location.href = '/login';
                    }
                }
            } else {
                // No refresh token available - clear auth and redirect
                console.warn('🔒 No refresh token available - clearing auth');
                stopTokenRefreshScheduler();
                useAuthStore.getState().clearAuth();

                if (!window.location.pathname.includes('/login')) {
                    window.location.href = '/login';
                }
            }
            break;

        case 403: {
            const errorCode =
                response.data &&
                typeof response.data === 'object' &&
                'error_code' in response.data &&
                typeof (response.data as { error_code?: unknown }).error_code === 'string'
                    ? (response.data as { error_code: string }).error_code
                    : undefined;

            if (errorCode === 'USER_INACTIVE') {
                // Inactive account should be treated like an auth-invalid session.
                stopTokenRefreshScheduler();
                localStorage.removeItem('accessToken');
                localStorage.removeItem('refreshToken');
                useAuthStore.getState().clearAuth();
                enqueueSnackbar(errorMessage || 'Your account is inactive. Please contact your administrator.', {
                    variant: 'error',
                    autoHideDuration: 5000,
                });
                if (!window.location.pathname.includes('/login')) {
                    window.location.href = '/login';
                }
                break;
            }

            // Forbidden (permissions/RBAC) — show API `detail` only.
            enqueueSnackbar(errorMessage || 'You do not have permission to perform this action', {
                variant: 'error',
                autoHideDuration: 5000,
            });
            break;
        }

        case 404:
            // Not Found
            enqueueSnackbar(errorMessage || 'Resource not found', {
                variant: 'error',
            });
            break;

        case 409:
            // Conflict
            enqueueSnackbar(errorMessage || 'Conflict occurred', {
                variant: 'warning',
            });
            break;

        case 422:
            // Validation Error
            enqueueSnackbar(errorMessage || 'Validation failed', {
                variant: 'error',
            });
            break;

        case 429:
            // Too Many Requests
            enqueueSnackbar('Too many requests. Please try again later.', {
                variant: 'warning',
                autoHideDuration: 6000,
            });
            break;

        case 500:
            // Internal Server Error
            enqueueSnackbar('Server error. Please try again later.', {
                variant: 'error',
                autoHideDuration: 5000,
            });
            break;

        case 502:
            // Bad Gateway
            enqueueSnackbar('Service temporarily unavailable', {
                variant: 'error',
                autoHideDuration: 5000,
            });
            break;

        case 503:
            // Service Unavailable
            enqueueSnackbar('Service is under maintenance. Please try again later.', {
                variant: 'warning',
                autoHideDuration: 6000,
            });
            break;

        case 504:
            // Gateway Timeout
            enqueueSnackbar('Request timeout. Please try again.', {
                variant: 'error',
                autoHideDuration: 5000,
            });
            break;

        default:
            // Generic error for other status codes
            if (status >= 500) {
                enqueueSnackbar('Server error occurred. Please try again later.', {
                    variant: 'error',
                });
            } else if (status >= 400) {
                enqueueSnackbar(errorMessage || 'Request failed', {
                    variant: 'error',
                });
            }
    }

    // TODO: Send error to monitoring service in production
    // Example: Sentry.captureException(error);

    return Promise.reject(error);
};

let _axiosInterceptorsInitialized = false;

/**
 * Register global axios interceptors (auth header, 401 refresh + login redirect on auth failure, errors).
 * Safe to call once at startup; repeated calls are ignored so Strict Mode cannot stack duplicates.
 */
export const initializeInterceptors = (): void => {
    if (_axiosInterceptorsInitialized) {
        return;
    }
    _axiosInterceptorsInitialized = true;

    axiosInstance.interceptors.request.use(requestInterceptor, requestErrorInterceptor);
    axiosInstance.interceptors.response.use(responseInterceptor, errorInterceptor);

    if (import.meta.env.DEV) {
        console.log('✅ Axios interceptors initialized');
    }
};
